import React from 'react';
import PropTypes from 'prop-types';
import ALink from 'tradera-components/alink/alink';
import MultiCurrency, {
    preferredCurrencyPropTypes
} from '../multi-currency/multi-currency';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { ITEM_CARD_IMAGE_MODES } from './item-card-parts/item-card-image-modes';
import AnimatedPriceLabel from './item-card-parts/animated-price-label';
import AnimatedTimeLeftLabel from './item-card-parts/animated-time-left-label';
import AttributeButtonsList from './item-card-parts/attribute-buttons-list';
import BidsLabel from './item-card-parts/bids-label';
import DiscountBadge from './item-card-parts/discount-badge';
import DiscountedPriceLabel from './item-card-parts/disacount-price-label';
import FixedPriceLabel from './item-card-parts/fixed-price-label';
import ItemCardImage from './item-card-parts/item-card-image';
import LeadingBadge from './item-card-parts/leading-badge';
import NewTodayLabel from './item-card-parts/new-today-label';
import OnDisplayBadge from './item-card-parts/on-display-badge';
import OverbiddenBadge from './item-card-parts/overbidden-badge';
import PriceLabel from './item-card-parts/price-label';
import QuickViewButton from './item-card-parts/quick-view-button';
import SettingsButton from './item-card-parts/settings-button';
import ShippingBadge from './item-card-parts/shipping-badge';
import TimeLeftLabel from './item-card-parts/time-left-label';
import WishListButton from './item-card-parts/wish-list-button';
import './item-card.scss';
import SellerAliasBadge from './item-card-parts/seller-alias-badge';

const ItemCard = ({
    animatePriceLabel,
    animateTimeLeft,
    animateTimeLeftWarningLimit,
    className,
    gaCategory,
    imageMode,
    item = {},
    lazy,
    onAddToWishlist,
    onQuickView,
    onRemoveFromWishlist,
    onAttributeButtonClick,
    onSettings,
    preferredCurrency,
    ready,
    showBidStatus,
    showShippingOrOnDisplay,
    showSellerAlias
}) => {
    let inSPA = true;
    const { existsInWishList } = item;
    const itemUrl = (item.url || item.itemUrl) && `${item.url || item.itemUrl}`;
    const imageUrl =
        item.imageUrl && item.imageUrl.replace('/medium/', '/medium-fit/');

    const handleSettings = event => {
        event.preventDefault();
        onSettings(item);
    };

    const handleQuickview = event => {
        event.preventDefault();
        onQuickView(item.itemId);
    };

    const handleWishlist = event => {
        event.preventDefault();
        if (existsInWishList) {
            onRemoveFromWishlist(item.itemId);
        } else {
            onAddToWishlist(item.itemId);
        }
    };

    const onNavigate = () => {
        if (gaCategory) {
            GtmService.trackAction(gaCategory, 'Navigation', item.itemId);
        }
    };

    return (
        <div
            id={`item-card-${item.itemId}`}
            data-item-loaded={ready ? 'true' : 'false'}
            className={`item-card-new
                ${ready ? '' : 'item-card-loading'}
                ${item.displayHighlighted ? 'item-card-new-highlight' : ''}
                ${className}
                `}>
            <div className="item-card-inner-wrapper">
                <ItemCardImage
                    className="mb-2 cursor-pointer"
                    onClick={onNavigate}
                    imageHeight={item.imageHeight}
                    imageMode={imageMode}
                    imageUrl={imageUrl}
                    imageWidth={item.imageWidth}
                    itemUrl={itemUrl}
                    lazy={lazy}
                    inSPA={inSPA}
                    shortDescription={item.shortDescription}>
                    <div className="item-card-top-badges">
                        {showSellerAlias && (
                            <SellerAliasBadge sellerAlias={item.sellerAlias} />
                        )}
                    </div>
                    <div className="item-card-bottom-badges">
                        {showShippingOrOnDisplay && item.showShipping && (
                            <ShippingBadge
                                className="mt-2 mr-2"
                                hasFreeShipping={item.hasFreeShipping}
                            />
                        )}
                        {showShippingOrOnDisplay &&
                            item.showOnDisplayRibbon && (
                                <OnDisplayBadge className="mt-2 mr-2" />
                            )}
                        {showBidStatus && item.isLeading && (
                            <LeadingBadge className="mt-2 float-right" />
                        )}
                        {showBidStatus && item.isOverbidden && (
                            <OverbiddenBadge className="mt-2 float-right" />
                        )}
                    </div>
                </ItemCardImage>
                <div className="item-card-buttons d-flex flex-column">
                    {ready && (
                        <>
                            <WishListButton
                                className={`item-card-wishlist-button btn-md-sm ${
                                    existsInWishList
                                        ? 'item-card-wishlist-button-active'
                                        : ''
                                } mb-1`}
                                disabled={!ready}
                                onClick={handleWishlist}
                                showText={false}
                                toggled={existsInWishList}
                            />
                            {onQuickView && (
                                <QuickViewButton
                                    className="item-card-quickview-button btn btn-md-sm btn-translucent mb-1 d-none d-lg-inline-block"
                                    onClick={handleQuickview}
                                />
                            )}
                            {item.showShopItemSettings && (
                                <SettingsButton
                                    className="item-card-settings-button btn btn-md-sm btn-translucent mb-1"
                                    data-shop-item-settings-action
                                    onClick={handleSettings}
                                />
                            )}
                        </>
                    )}
                </div>
                <p className="item-card-title d-flex flex-row mb-1">
                    <ALink
                        onClick={onNavigate}
                        className="font-weight-normal text-truncate-two-lines"
                        href={item.url || item.itemUrl}
                        id={`item_${item.itemId}`}
                        role="button"
                        title={item.shortDescription}
                        useLink={inSPA}>
                        {item.hasDiscount && (
                            <DiscountBadge
                                className="mr-2 badge-inline"
                                discountRate={item.discountRate}
                            />
                        )}
                        {item.shortDescription}
                    </ALink>
                </p>
                {item.itemAttributesForFilter && onAttributeButtonClick && (
                    <p className="item-card-title d-flex flex-row mb-1">
                        <AttributeButtonsList
                            itemAttributesForFilter={
                                item.itemAttributesForFilter
                            }
                            onClick={onAttributeButtonClick}
                        />
                    </p>
                )}
                <div className="item-card-details">
                    {item.hasDiscount && (
                        <DiscountedPriceLabel
                            className="d-inline-block text-nowrap mr-2"
                            price={item.priceBeforeDiscount}
                        />
                    )}
                    {animatePriceLabel ? (
                        <AnimatedPriceLabel
                            className="d-inline-block text-nowrap font-weight-bold item-card-details-price"
                            price={item.currentBid || item.price}
                            hasDiscount={item.hasDiscount}
                        />
                    ) : (
                        <PriceLabel
                            className="d-inline-block text-nowrap font-weight-bold item-card-details-price"
                            price={item.currentBid || item.price}
                            hasDiscount={item.hasDiscount}
                        />
                    )}
                    {item.isShopItem ? (
                        <FixedPriceLabel className="d-inline-block text-nowrap float-right" />
                    ) : animateTimeLeft ? (
                        <AnimatedTimeLeftLabel
                            className="d-inline-block text-nowrap float-right text-gray-600"
                            countdownWarningLimit={animateTimeLeftWarningLimit}
                            endDate={item.endDateForCountdown || item.endDate}
                            timeLeft={item.timeLeft}
                        />
                    ) : (
                        <TimeLeftLabel
                            className="d-inline-block float-right text-nowrap"
                            isEndingSoon={item.isEndingSoon}
                            timeLeft={item.timeLeft}
                        />
                    )}
                    <MultiCurrency
                        className="d-inline-block text-nowrap mr-2"
                        amountInSek={item.price}
                        preferredCurrency={preferredCurrency}
                    />
                    {MultiCurrency.isVisible(item.price, preferredCurrency) &&
                        !item.isShopItem &&
                        item.showBids && (
                            <span className="text-gray-400 mr-2">|</span>
                        )}
                    {!item.isShopItem && item.showBids && (
                        <BidsLabel
                            className="d-inline-block text-nowrap mr-2"
                            count={item.totalBids}
                        />
                    )}
                    {item.isNewToday && (
                        <NewTodayLabel className="d-inline-block text-nowrap mr-2" />
                    )}
                </div>
            </div>
        </div>
    );
};

ItemCard.defaultProps = {
    animatePriceLabel: false,
    animateTimeLeft: false,
    className: '',
    gaCategory: null,
    imageMode: ITEM_CARD_IMAGE_MODES.SQUARE,
    onAddToWishlist: () => {},
    onRemoveFromWishlist: () => {},
    onQuickView: null,
    showBidStatus: false,
    showShippingOrOnDisplay: true,
    showSellerAlias: false
};

ItemCard.propTypes = {
    animatePriceLabel: PropTypes.bool,
    animateTimeLeft: PropTypes.bool,
    animateTimeLeftWarningLimit: PropTypes.number,
    className: PropTypes.string,
    gaCategory: PropTypes.string,
    imageMode: PropTypes.oneOf(Object.values(ITEM_CARD_IMAGE_MODES)),
    item: PropTypes.shape({
        buyNowPrice: PropTypes.number,
        currentBid: PropTypes.number,
        discountRate: PropTypes.number,
        displayHighlighted: PropTypes.bool,
        existsInWishList: PropTypes.bool,
        hasDiscount: PropTypes.bool,
        hasNonSpecifiedShipping: PropTypes.bool,
        imageHeight: PropTypes.number,
        imageUrl: PropTypes.string,
        imageWidth: PropTypes.number,
        isEndingSoon: PropTypes.bool,
        isNewToday: PropTypes.bool,
        isShopItem: PropTypes.bool,
        itemId: PropTypes.number,
        itemUrl: PropTypes.string,
        prettifyRating: PropTypes.shape({
            includeTotal: PropTypes.bool,
            isBad: PropTypes.bool,
            isGood: PropTypes.bool,
            noRating: PropTypes.bool,
            rating: PropTypes.string
        }),
        price: PropTypes.number,
        priceBeforeDiscount: PropTypes.number,
        sellerAlias: PropTypes.string,
        sellerDsrAverage: PropTypes.number,
        shortDescription: PropTypes.string,
        showBids: PropTypes.bool,
        showBuyNow: PropTypes.bool,
        showFreeShippingRibbon: PropTypes.bool,
        showOnDisplayRibbon: PropTypes.bool,
        showShopItemSettings: PropTypes.bool,
        showSmbMessage: PropTypes.bool,
        timeLeft: PropTypes.string,
        url: PropTypes.string
    }),
    lazy: PropTypes.shape({
        offset: PropTypes.number,
        once: PropTypes.bool,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        placeholder: PropTypes.node,
        imagePlaceholder: PropTypes.node
    }),
    onAddToWishlist: PropTypes.func,
    onRemoveFromWishlist: PropTypes.func,
    onQuickView: PropTypes.func,
    onSettings: PropTypes.func,
    preferredCurrency: preferredCurrencyPropTypes,
    ready: PropTypes.bool,
    showBidStatus: PropTypes.bool,
    showShippingOrOnDisplay: PropTypes.bool
};

export default ItemCard;
